import mixpanel from 'mixpanel-browser';

// ---------------------------------------------------------------------------
// Mixpanel tracking plan. The plan assigns every event to exactly one owning
// repository; only the rows owned by the frontend live here, under the plan's
// own snake_case names. Everything else (listing, review, social posting,
// policy, wordpress, jooice-ai) is emitted by the repository that owns it.
// ---------------------------------------------------------------------------

export interface IdentifyProperties {
  assigned_flow?: 'review' | 'social';
  billing_stage?: string;
  experiment_score_copy?: 'emotional' | 'operational';
  is_spartacus?: boolean;
  main_goal?: string;
  outseta_country?: 'ita' | 'usa' | 'mea' | 'spartacus';
  user_id?: string;
  partner?: string;
  plan_id?: string;
  plan_type?: string;
  renewal_terms?: number;
  subscription_type?: string;
  tipologia_cliente?: string;
  trial_status?: string;
  has_connected_social?: boolean;
  num_connected_platforms?: number;
  connected_platforms?: string[];
  last_creation_method?: 'template' | 'ai' | 'blank';
  social_posts_published_count?: number;
  scheduled_posts_count?: number;
  ai_posts_generated_count?: number;
  num_location?: number;
}

/** Plan row `ai_response_accepted` (Usage / Frontend). */
export interface AiResponseAcceptedProperties {
  section_name?: string;
  bulk?: boolean;
  count?: number;
}

/** Plan row `onboarding_step_viewed` (Acquisition / Frontend). */
export interface OnboardingStepViewedProperties {
  step_key: string;
  step_index: number;
  onboarding_version: number;
  organization_id?: number | null;
}

/**
 * Not a row of the plan. Kept because the listing-review campaign-stats endpoint
 * reads `Review Campaign Response` and `Review Campaign Viewed` back by name through
 * the Mixpanel Query API (`review-campaigns-analytics.service.ts`). Renaming or
 * dropping either one zeroes that endpoint with no error anywhere.
 */
export interface ReviewCampaignResponseProperties {
  positive: boolean;
  slug: string;
}

/** Read back by name alongside `ReviewCampaignResponseProperties`. */
export interface ReviewCampaignViewedProperties {
  slug?: string;
}

/** Plan row `upgrade_cta_clicked` (Usage / Frontend). */
export interface UpgradeCtaClickedProperties {
  is_grace_period?: boolean;
  source?: string;
}

/**
 * Plan row `signup_started` (Acquisition / Frontend). Properties come verbatim from the
 * /purchase query string; a missing param is absent, never defaulted.
 */
export interface SignupStartedProperties {
  planKey?: string;
  interval?: string;
  market?: string;
}

let mixpanelInitialized = false;
/** Callbacks waiting for the consent-gated init; flushed once by initMixpanel. */
const readyCallbacks: Array<() => void> = [];
/** Last distinct_id we identified, for the identity-invariant guard below. */
let currentDistinctId: string | undefined;

/** Identify-time traits that should ride every event as super-properties. */
const SUPER_PROP_KEYS = ['is_spartacus', 'outseta_country', 'user_id'] as const;

/**
 * Boot the Mixpanel browser SDK. No-op without a token, or when already booted.
 */
export const initMixpanel = (token: string | undefined, options?: Parameters<typeof mixpanel.init>[1]) => {
  if (mixpanelInitialized) {
    return;
  }

  if (!token) {
    return;
  }

  mixpanel.init(token, {
    track_pageview: false,
    persistence: 'localStorage',
    record_sessions_percent: 100,
    ...options,
  });

  mixpanelInitialized = true;

  for (const callback of readyCallbacks.splice(0)) {
    callback();
  }
};

/** True once initMixpanel has run with a token. */
export const isMixpanelReady = () => mixpanelInitialized;

/**
 * Run `callback` now when Mixpanel is already initialised, otherwise right after the
 * consent-gated init. Never runs if the visitor does not grant measurement/marketing.
 */
export const whenMixpanelReady = (callback: () => void) => {
  if (mixpanelInitialized) {
    callback();
    return;
  }

  readyCallbacks.push(callback);
};

const trackEvent = (eventName: string, properties?: object, options?: { transport?: 'xhr' | 'sendBeacon' }) => {
  if (!mixpanelInitialized) {
    return;
  }

  try {
    const props = properties as Record<string, unknown> | undefined;

    if (options) {
      mixpanel.track(eventName, props, options);
    } else {
      mixpanel.track(eventName, props);
    }
  } catch (err) {
    console.warn(`[mixpanel] track("${eventName}") failed`, err);
  }
};

/** Drop the current identity and its super-properties, on logout. */
export const mixpanelReset = () => {
  if (!mixpanelInitialized) {
    return;
  }

  try {
    mixpanel.reset();
    currentDistinctId = undefined;
  } catch (err) {
    console.warn('[mixpanel] reset failed', err);
  }
};

/** Register always-on super-properties carried on every subsequent event. */
const register = (properties: Record<string, unknown>) => {
  if (!mixpanelInitialized) {
    return;
  }

  try {
    mixpanel.register(properties);
  } catch (err) {
    console.warn('[mixpanel] register failed', err);
  }
};

const identify = (userId: string | undefined, properties?: IdentifyProperties) => {
  if (!mixpanelInitialized) {
    return;
  }

  try {
    if (userId && currentDistinctId && currentDistinctId !== userId) {
      // Identity-invariant guard: switching to a different distinct_id without a
      // reset would merge the new user onto the previous identity (and leak the
      // previous user's super-props). Reset first so each user is clean.
      mixpanel.reset();
    }

    if (userId) {
      mixpanel.identify(userId);
      currentDistinctId = userId;
      // The plan asks for an explicit `user_id` on every event, not only the
      // implicit distinct_id.
      mixpanel.register({ user_id: userId });
    }

    if (properties && Object.keys(properties).length > 0) {
      // Mirror the always-on segmentation traits as super-props so they ride
      // every event at fire time, not just the people profile.
      const superProps: Record<string, unknown> = {};

      mixpanel.people.set(properties as Record<string, unknown>);

      for (const key of SUPER_PROP_KEYS) {
        const value = (properties as Record<string, unknown>)[key];

        if (value !== undefined) {
          superProps[key] = value;
        }
      }

      if (Object.keys(superProps).length > 0) {
        mixpanel.register(superProps);
      }
    }
  } catch (err) {
    console.warn('[mixpanel] identify failed', err);
  }
};

/**
 * Typed Mixpanel client: one method per frontend-owned event in the tracking plan.
 */
export const mxp = {
  identify,
  register,
  aiResponseAccepted: (properties?: AiResponseAcceptedProperties) => trackEvent('ai_response_accepted', properties),
  onboardingStepViewed: (properties: OnboardingStepViewedProperties) =>
    trackEvent('onboarding_step_viewed', properties),
  reviewCampaignResponse: (properties: ReviewCampaignResponseProperties) =>
    trackEvent('Review Campaign Response', properties),
  reviewCampaignViewed: (properties?: ReviewCampaignViewedProperties) =>
    trackEvent('Review Campaign Viewed', properties),
  upgradeCtaClicked: (properties?: UpgradeCtaClickedProperties) => trackEvent('upgrade_cta_clicked', properties),
  signupStarted: (properties: SignupStartedProperties) =>
    trackEvent('signup_started', properties, { transport: 'sendBeacon' }),
};

export { mixpanel };
